import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import api, { API } from "@/lib/api";
import { Loader2, Upload, RotateCcw } from "lucide-react";

const fileSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export const DEFAULT_BANNER =
  "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export const BannerManager = () => {
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/site/banner").then((r) => setBanner(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(""); setStatus("");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Please choose a JPEG, PNG or WebP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("That image is larger than 8 MB — please choose a smaller file.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/admin/banner", form, { headers: { "Content-Type": "multipart/form-data" } });
      setBanner(r.data);
      if (r.data?.url) localStorage.setItem("cf_banner_src", `${API.replace(/\/api$/, "")}${r.data.url}`);
      setStatus(`“${file.name}” is now live on the homepage.`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setError(""); setStatus(""); setBusy(true);
    try {
      const r = await api.delete("/admin/banner");
      setBanner(r.data);
      localStorage.removeItem("cf_banner_src");
      setStatus("Back to the built-in default image.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not revert — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const src = banner && !banner.is_default ? `${API.replace(/\/api$/, "")}${banner.url}` : DEFAULT_BANNER;

  return (
    <div className="bg-white rounded-2xl border border-brand-cream2 p-6" data-testid="admin-banner-panel">
      <h3 className="font-serif text-xl mb-1">Homepage banner image</h3>
      <p className="text-sm text-muted-foreground mb-5">
        A wide, landscape photograph works best. JPEG, PNG or WebP, up to 8 MB — large photographs are
        fine, they're optimised automatically.
      </p>

      <div className="flex flex-col sm:flex-row gap-5">
        <img src={src} alt="Current homepage banner" data-testid="admin-banner-preview"
          className="w-full sm:w-72 h-40 object-cover rounded-xl border border-brand-cream2" />
        <div className="flex-1">
          <p className="text-sm font-medium" data-testid="admin-banner-state">
            {banner === null ? "Loading…" : banner.is_default
              ? "Currently using the built-in default image."
              : `Currently using an uploaded image${banner.filename ? ` (${banner.filename})` : ""}.`}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
              className="hidden" onChange={onPick} data-testid="admin-banner-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy}
              data-testid="admin-banner-upload-button"
              className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload new image
            </Button>
            {banner && !banner.is_default && (
              <Button onClick={revert} variant="outline" disabled={busy}
                data-testid="admin-banner-revert-button" className="rounded-full gap-2">
                <RotateCcw className="w-4 h-4" /> Use the default image
              </Button>
            )}
          </div>
          {busy && <p className="mt-3 text-xs text-muted-foreground" data-testid="admin-banner-progress">Uploading…</p>}
          {banner && !banner.is_default && banner.size && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="admin-banner-size">
              {banner.resized
                ? `Now serving ${fileSize(banner.size)} (resized from ${fileSize(banner.original_size || banner.size)}).`
                : `Now serving ${fileSize(banner.size)} — the full-size image.`}
            </p>
          )}
          {error && <p className="mt-3 text-sm text-brand-wine" data-testid="admin-banner-error">{error}</p>}
          {status && !error && <p className="mt-3 text-sm text-brand-green" data-testid="admin-banner-status">{status}</p>}
        </div>
      </div>
    </div>
  );
};
